import classNames from "classnames";
import { ActionIcon } from "PFComponents/action_icon";
import { Tabs } from "PFComponents/tabs";
import { CurrentProfile, Profile, ProfileCustomValue } from "PFTypes";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToggle } from "react-use";

import { DraggableSkill } from "../draggable_skill";
import { SkillsFrameworksSuggestedSkillsModal } from "./skills_frameworks_suggested_skills_modal";
import css from "./skills_suggestions.module.scss";

type SkillTab = {
  id: string;
  title: string;
  skills: Partial<ProfileCustomValue>[];
  disabled?: boolean;
  infoText?: string;
};

type SkillsSuggestionsProps = {
  profile: Profile | CurrentProfile;
  addToLog: (...changes: any[]) => void;
  onSkillRanked: (customValue: ProfileCustomValue) => void;
  tabs: SkillTab[];
  isOpen?: boolean;
  style?: CSSProperties;
};

export const SkillsSuggestions = ({
  profile,
  addToLog,
  onSkillRanked,
  tabs,
  isOpen = true,
  style
}: SkillsSuggestionsProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation("profiles", { keyPrefix: "show.parts" });
  const [isSkillsFrameworksModalOpened, toggleSkillsFrameworksModalOpened] = useToggle(false);

  const [selectedTabId, setSelectedTabId] = useState<string>(tabs[0].id);
  const [open, setOpen] = useState<boolean>(isOpen);
  const [height, setHeight] = useState<number>(0);

  const selectedTab = tabs.find(({ id }) => id === selectedTabId) || tabs[0];
  const { id: parentSection, disabled, skills } = selectedTab;
  const isFrameworkSuggestedSkills = selectedTab.id === "framework";

  useEffect(() => {
    setHeight(open ? ref.current?.clientHeight || 0 : 0);
  }, [open, selectedTab.id]);

  const toggle = () => setOpen((open) => !open);

  return (
    <div className={classNames(css.skillsSuggestions, { [css.disabled]: disabled })} style={style}>
      <div className={css.header}>
        <Tabs<string>
          tabs={tabs}
          activeTab={selectedTab.id}
          onChange={setSelectedTabId}
          className={css.pillTabs}
        />
        <ActionIcon
          name={open ? "chevron-up" : "chevron-down"}
          size="sm"
          classes={{ root: css.icon }}
          onClick={toggle}
          aria-label={
            open ? t("skillsModal.collapseSkillsSuggestions") : t("skillsModal.revealSkillsSuggestions")
          }
        />
      </div>
      <div className={css.heightController} style={{ height }}>
        <div ref={ref} className={css.main}>
          <div className={css.suggestedSkillsHeader}>
            <div className={css.info}>
              {selectedTab.infoText && <b>{selectedTab.infoText}</b>}
              <div>
                {selectedTabId === "discovered"
                  ? t("skillsModal.suggestions.discovered.info")
                  : t("skillsModal.suggestions.info")}
              </div>
            </div>
            {isFrameworkSuggestedSkills && (
              <ActionIcon
                name="open"
                size="sm"
                onClick={toggleSkillsFrameworksModalOpened}
                classes={{ root: css.linkToModalIcon }}
                aria-label={t("openSkillsFrameworksSuggestionsModal")}
              />
            )}
          </div>
          <div className={classNames(css.additionalSkills, css.suggestedSkills)}>
            <div className={css.skillsWrapper}>
              {skills.map((skill, index) => (
                <DraggableSkill
                  key={`suggested-${skill.id}`}
                  skill={skill}
                  index={index}
                  addToLog={addToLog}
                  parentSection={parentSection}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      {isSkillsFrameworksModalOpened && (
        <SkillsFrameworksSuggestedSkillsModal
          profile={profile}
          canEditSkills
          onClose={toggleSkillsFrameworksModalOpened}
          onSkillRanked={onSkillRanked}
        />
      )}
    </div>
  );
};
